/**
 * Validation utilities for form validation
 * Provides email, password, phone number validation and formatting
 */

/**
 * Validate email format
 * @param {string} email - Email to validate
 * @returns {Object} { isValid: boolean, error: string }
 */
export const validateEmail = (email) => {
  if (!email || email.trim() === '') {
    return { isValid: false, error: 'Email wajib diisi' };
  }

  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  if (!emailRegex.test(email)) {
    return { isValid: false, error: 'Format email tidak valid' };
  }

  // Optional: Check for common invalid domains
  const invalidDomains = ['test.com', 'example.com', 'invalid.com'];
  const domain = email.split('@')[1]?.toLowerCase();
  if (domain && invalidDomains.includes(domain)) {
    return { isValid: false, error: 'Gunakan email yang valid' };
  }

  return { isValid: true, error: '' };
};

/**
 * Calculate password strength
 * @param {string} password - Password to check
 * @returns {Object} { strength: 'weak'|'medium'|'strong', score: number, feedback: string[] }
 */
export const checkPasswordStrength = (password) => {
  if (!password) {
    return { strength: 'weak', score: 0, feedback: [] };
  }

  let score = 0;
  const feedback = [];

  // Length check
  if (password.length >= 8) {
    score += 1;
  } else {
    feedback.push('Minimal 8 karakter');
  }

  // Lowercase check
  if (/[a-z]/.test(password)) {
    score += 1;
  } else {
    feedback.push('Gunakan huruf kecil');
  }

  // Uppercase check
  if (/[A-Z]/.test(password)) {
    score += 1;
  } else {
    feedback.push('Gunakan huruf besar');
  }

  // Number check
  if (/\d/.test(password)) {
    score += 1;
  } else {
    feedback.push('Gunakan angka');
  }

  // Special character check
  if (/[!@#$%^&*(),.?":{}|<>]/.test(password)) {
    score += 1;
  } else {
    feedback.push('Gunakan karakter khusus');
  }

  // Determine strength
  let strength = 'weak';
  if (score >= 4) {
    strength = 'strong';
  } else if (score >= 2) {
    strength = 'medium';
  }

  return { strength, score, feedback: feedback.slice(0, 3) }; // Show max 3 feedback items
};

/**
 * Format Indonesian phone number
 * @param {string} phone - Phone number to format
 * @returns {string} Formatted phone number
 */
export const formatPhoneNumber = (phone) => {
  if (!phone) return '';

  // Remove all non-digit characters
  const digits = phone.replace(/\D/g, '');

  // If starts with 0, keep it
  if (digits.startsWith('0')) {
    return digits;
  }

  // If starts with 62, convert to 0
  if (digits.startsWith('62')) {
    return '0' + digits.substring(2);
  }

  // If starts with +62, convert to 0
  if (phone.startsWith('+62')) {
    return '0' + digits.substring(2);
  }

  return digits;
};

/**
 * Validate Indonesian phone number
 * @param {string} phone - Phone number to validate
 * @returns {Object} { isValid: boolean, error: string }
 */
export const validatePhoneNumber = (phone) => {
  if (!phone || phone.trim() === '') {
    return { isValid: true, error: '' }; // Phone is optional
  }

  const digits = phone.replace(/\D/g, '');
  
  // Indonesian phone numbers should be 10-13 digits (including leading 0)
  if (digits.length < 10 || digits.length > 13) {
    return { isValid: false, error: 'Nomor telepon harus 10-13 digit' };
  }

  // Should start with 0 or 62
  if (!digits.startsWith('0') && !digits.startsWith('62')) {
    return { isValid: false, error: 'Format nomor telepon tidak valid' };
  }

  return { isValid: true, error: '' };
};

/**
 * Validate name
 * @param {string} name - Name to validate
 * @returns {Object} { isValid: boolean, error: string }
 */
export const validateName = (name) => {
  if (!name || name.trim() === '') {
    return { isValid: false, error: 'Nama wajib diisi' };
  }

  if (name.trim().length < 2) {
    return { isValid: false, error: 'Nama minimal 2 karakter' };
  }

  if (name.trim().length > 100) {
    return { isValid: false, error: 'Nama maksimal 100 karakter' };
  }

  return { isValid: true, error: '' };
};

/**
 * Validate password confirmation
 * @param {string} password - Original password
 * @param {string} confirmPassword - Confirmation password
 * @returns {Object} { isValid: boolean, error: string }
 */
export const validatePasswordConfirmation = (password, confirmPassword) => {
  if (!confirmPassword || confirmPassword.trim() === '') {
    return { isValid: false, error: 'Konfirmasi password wajib diisi' };
  }

  if (password !== confirmPassword) {
    return { isValid: false, error: 'Password tidak cocok' };
  }

  return { isValid: true, error: '' };
};
